import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { useReadFeatureFlags } from '../api/readFeatureFlags';
import type { FeatureFlag } from '../domain/featureFlag';
import type { FeatureFlagResponse } from '../api/models/featureFlagResponse';
import { useRefreshFeatureFlags } from '../api/refreshFeatureFlags';
import { useToggleFeatureFlags } from '../api/toggleFeatureFlag';
import { useSelectedSiteStore } from '@/sites/stores/selectedSiteStore';

export const useFeatureFlagStore = defineStore('featureflags', () => {
  const emptyFeatureFlags: FeatureFlagResponse = {
    featureFlags: [
      {
        name: 'dummy',
        isEnabled: false,
      },
    ] as FeatureFlag[],
  };
  const featureFlags = ref(emptyFeatureFlags);

  const readFeatureFlags = useReadFeatureFlags();
  const initialized = ref(false);
  const initializing = ref(false);
  const actionFailedAlert = useActionFailedAlert();
  const siteStore = useSelectedSiteStore();

  const refreshFeatureFlags = useRefreshFeatureFlags();

  const toggleFeatureFlag = useToggleFeatureFlags();

  async function initialize() {
    if (!siteStore.siteId) return;
    if (initialized.value) return;
    try {
      initializing.value = true;

      const response = await readFeatureFlags.execute();

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      featureFlags.value = response.featureFlag;
      initialized.value = true;
    } finally {
      initializing.value = false;
    }
  }

  async function refresh() {
    const response = await refreshFeatureFlags.execute();

    if (!response.ok) {
      actionFailedAlert.show(response.details);
      return false;
    }

    featureFlags.value = response.featureFlag;
    return true;
  }

  async function toggle(feature: string, isEnabled: boolean) {
    const response = await toggleFeatureFlag.execute({ Feature: feature, IsEnabled: isEnabled });

    if (!response.ok) {
      actionFailedAlert.show(response.details);
      return false;
    }

    featureFlags.value = response.featureFlag;
    return true;
  }

  function isFeatureEnabled(feature: string): boolean {
    return featureFlags.value.featureFlags.some((f) => f.name === feature && f.isEnabled);
  }

  function isCoilEnabled() {
    return isFeatureEnabled('Coil');
  }

  function isLogYardEnabled() {
    return isFeatureEnabled('LogYard');
  }

  function isBulkEnabled() {
    return isFeatureEnabled('Bulk');
  }

  return {
    initialize,
    refresh,
    toggle,
    featureFlags,
    isFeatureEnabled,
    isCoilEnabled,
    isLogYardEnabled,
    isBulkEnabled
  };
});
